import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class ICDiagnosticsService {

  constructor(private http: HttpClient) { }

  /**
   * Get Entity Details
   * Action code: xsivl3
   */
  getEntityDetails(baseUrl: string, sso_id: string, globalService: any = {}, filterParams: any = {}): Observable<any> {
    // Merge globalParams and filterParams (like AngularJS JsonObjectFactory does)
    let params = _.merge({}, globalService || {}, filterParams);
    
    // Add specific parameters for this API call
    params.action_code = 'xsivl3';
    params.sso_id = sso_id;

    // Build query string parameters (like AngularJS)
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        queryParams.set(key, params[key].toString());
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get<any>(url, { withCredentials: true });
  }

  /**
   * Get IC Outside Account Details
   * Action code: zjm7kq
   */
  getICOutsideAcctDetails(
    baseUrl: string, 
    combination_key: string, 
    sso_id: string,
    globalService: any = {},
    filterParams: any = {}
  ): Observable<any> {
    // Merge globalParams and filterParams (like AngularJS JsonObjectFactory does)
    let params = _.merge({}, globalService || {}, filterParams);
    
    // Add specific parameters for this API call
    params.action_code = 'zjm7kq';
    params.sso_id = sso_id;
    params.combination_key = combination_key;
    params.form_no = 'IC_DIAG';

    // Build query string parameters (like AngularJS)
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        queryParams.set(key, params[key].toString());
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get<any>(url, { withCredentials: true });
  }

  /**
   * Get BSLA (Component) Details
   * Action code: 33qzsa
   */
  getBSLADetails(
    baseUrl: string, 
    sso_id: string, 
    combination_key: string,
    globalService: any = {},
    filterParams: any = {}
  ): Observable<any> {
    // Merge globalParams and filterParams (like AngularJS JsonObjectFactory does)
    let params = _.merge({}, globalService || {}, filterParams);
    
    // Add specific parameters for this API call
    params.action_code = '33qzsa';
    params.sso_id = sso_id;
    params.combination_key = combination_key;

    // Build query string parameters (like AngularJS)
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        queryParams.set(key, params[key].toString());
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get<any>(url, { withCredentials: true });
  }

  /**
   * Get Counter Party Form Details
   * Action code: 1d50rl
   */
  getCPFormDetails(
    baseUrl: string, 
    combination_key: string, 
    form_name: string, 
    schedule_name: string, 
    sso_id: string,
    globalService: any = {},
    filterParams: any = {}
  ): Observable<any> {
    // Merge globalParams and filterParams (like AngularJS JsonObjectFactory does)
    let params = _.merge({}, globalService || {}, filterParams);
    
    // Add specific parameters for this API call
    params.action_code = '1d50rl';
    params.sso_id = sso_id;
    params.combination_key = combination_key;
    params.form_name = form_name;
    params.schedule_name = schedule_name;

    // Build query string parameters (like AngularJS)
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        queryParams.set(key, params[key].toString());
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get<any>(url, { withCredentials: true });
  }

  /**
   * Save IC Form
   * Action code: 8t0ocw
   */
  saveICForm(
    baseUrl: string,
    finalData: any[],
    clobSettings: any[],
    formName: string,
    scheduleName: string,
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=8t0ocw`;

    // Build payload structure exactly like in original AngularJS
    let params = _.merge({}, globalService || {}, filterParams);
    
    // Add json_data and json_settings (Note: singular "json_settings" in AngularJS)
    params = _.extend({ json_data: JSON.stringify(finalData) }, params);
    params = _.extend({ json_settings: JSON.stringify(clobSettings) }, params);
    
    // Add process name, form name, and schedule name
    params.process_name = "IC-BU-Allocation-Save";
    params.form_name = formName;
    params.schd_name = scheduleName;

    // Add tax_year, scenario, jcd_key with fallbacks
    params.tax_year = filterParams.tax_year || params.tax_year;
    params.scenario = filterParams.scenario || params.scenario;
    params.jcd_key = filterParams.jcd_key || params.jcd_key || 250;
    params.scenarioCode = filterParams.scenarioCode || filterParams.scenario_code || params.scenarioCode || "RAF";
    params.is_issue_key_enabled = "N";

    // Build checkLockedbj from rowData (matches gridController.js structure)
    const checkLockedbj = [{
      "leid": rowData.LEID,
      "me_code": rowData.ME_CODE || rowData.ME || rowData.MAINTENANCE_OWNER || rowData.SOURCE_ME_CODES,
      "cdr_number": rowData.CDR_NO || rowData.TAX_CODE,
      "consol_group_key": rowData.CONSOL_GROUP_KEY || rowData.FILING_GROUP,
      "reporting_period": rowData.REPORTING_PERIOD,
      "combination_key": rowData.COMBINATION_KEY || rowData.HO_COMBINATION_KEY || rowData.COMPL_COMBINATION_KEY,
      "scenario_key": parseInt(filterParams.scenario || params.scenario || rowData.SCENARIO_KEY || 74)
    }];
    params.checkLockedbj = JSON.stringify(checkLockedbj);

    // Convert params to URLSearchParams for form-urlencoded format
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }
}

